import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { OpsOrderItemCategory } from '@/graphql/generatedTypes';
import {
  PickingJobPickedItemsQuery,
  usePickingJobPickedItemsQuery,
} from '@/graphql/queries/pickingJobPickedItems.generated';
import {
  CanAccessContainerStation,
  CanAccessPartnerData,
  Viewer,
  ViewerContext,
} from '@/providers/ViewerContextProvider';
import { Routes } from '@/utils/routes';
import { OrderPagination } from '@/views/Order/components/table/pagination';
import { formatDateTimeLocalized } from '@/utils/formatter';
import i18n, { formatNumber } from '@/utils/i18n';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

type PickedItem =
  PickingJobPickedItemsQuery['getPickingJobPickedItemsPaginated']['items'][number];

/**
 * The detail page of the scanned container, station or trackable item, or
 * null when the row has none (untracked items) or the viewer may not open it.
 * The guards are the ones AppShellWrapper puts on those routes.
 */
function pickedItemHref(item: PickedItem, viewer: Viewer): string | null {
  if (!item.reference_id) return null;
  switch (item.kind) {
    case OpsOrderItemCategory.Container:
      return CanAccessPartnerData(viewer)
        ? Routes.container(item.reference_id)
        : null;
    case OpsOrderItemCategory.ContainerStation:
      return CanAccessContainerStation(viewer)
        ? Routes.containerStation(item.reference_id)
        : null;
    case OpsOrderItemCategory.Item:
      return CanAccessPartnerData(viewer)
        ? Routes.trackableItem(item.reference_id)
        : null;
    default:
      return null;
  }
}

function shortReference(referenceId: string): string {
  return referenceId.length > 8 ? `${referenceId.slice(0, 8)}…` : referenceId;
}

/**
 * The containers and container stations actually scanned onto this job — one
 * row per scan, which is what answers "where did this container go".
 *
 * Search is server-side: names match as a substring, and the six-character
 * station and trackable ids and a container's ShortId match as an exact code.
 * A container's uuid is not searchable.
 *
 * A container, station or trackable-item row links to that thing's detail
 * page when the viewer may open it.
 *
 * Render it with `key={pickingJobId}` so search text and page do not carry
 * over when the detail page switches to another job.
 */
export function PickedItemsCard({ pickingJobId }: { pickingJobId: string }) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const viewer = useContext(ViewerContext);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading, error, previousData } = usePickingJobPickedItemsQuery({
    variables: {
      picking_job_id: pickingJobId,
      search: debouncedSearch || undefined,
      skip: (page - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
  });

  // Keep the previous page on screen while the next one loads, so the table
  // does not collapse to skeletons on every keystroke.
  const result = data?.getPickingJobPickedItemsPaginated ??
    previousData?.getPickingJobPickedItemsPaginated ?? { items: [], total: 0 };
  const totalPages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
  const showSkeleton = loading && !previousData && !error;

  return (
    <Card>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
          <h2 className="tw:text-lg tw:font-semibold">
            {i18n.t('Picking:detailPickedItems')}
          </h2>
          <Input
            className="tw:w-[260px]"
            placeholder={i18n.t('Picking:detailSearchPlaceholder')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="tw:text-sm tw:text-muted-foreground">
          {i18n.t('Order:resultCount', {
            shown: formatNumber(result.items.length),
            total: formatNumber(result.total),
          })}
        </div>

        <div className="tw:rounded-md tw:border tw:overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{i18n.t('Picking:detailColumnName')}</TableHead>
                <TableHead>{i18n.t('Picking:detailColumnReference')}</TableHead>
                <TableHead>{i18n.t('Picking:detailColumnAmount')}</TableHead>
                <TableHead>{i18n.t('Picking:detailColumnAddedAt')}</TableHead>
                <TableHead>{i18n.t('Picking:detailColumnAddedBy')}</TableHead>
                <TableHead>{i18n.t('Picking:detailColumnDelivered')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showSkeleton &&
                Array.from({ length: 5 }).map((_, rowIndex) => (
                  <TableRow key={`picked-skeleton-${rowIndex}`}>
                    {Array.from({ length: 6 }).map((__, cellIndex) => (
                      <TableCell
                        key={`picked-skeleton-${rowIndex}-${cellIndex}`}
                      >
                        <Skeleton className="tw:h-5 tw:w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {error && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="tw:h-24 tw:text-center tw:text-destructive"
                  >
                    {i18n.t('Common:error')}
                  </TableCell>
                </TableRow>
              )}

              {!error && !showSkeleton && result.items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="tw:h-24 tw:text-center tw:text-muted-foreground"
                  >
                    {i18n.t('Common:noResults')}
                  </TableCell>
                </TableRow>
              )}

              {!error &&
                !showSkeleton &&
                result.items.map((item) => {
                  const fullyDelivered =
                    item.delivered_amount >= item.amount && item.amount > 0;
                  const href = pickedItemHref(item, viewer);
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        {href ? (
                          <Link
                            to={href}
                            className="tw:font-medium tw:underline-offset-4 tw:hover:underline"
                          >
                            {item.name ??
                              shortReference(item.reference_id ?? '')}
                          </Link>
                        ) : (
                          (item.name ?? '-')
                        )}
                      </TableCell>
                      <TableCell
                        className="tw:font-mono tw:text-xs"
                        title={item.reference_id ?? undefined}
                      >
                        {item.reference_id
                          ? shortReference(item.reference_id)
                          : '-'}
                      </TableCell>
                      <TableCell>{item.amount}</TableCell>
                      <TableCell>
                        {formatDateTimeLocalized(
                          new Date(item.added_at),
                          i18n.language,
                        )}
                      </TableCell>
                      <TableCell>{item.added_by?.name || '-'}</TableCell>
                      <TableCell>
                        {fullyDelivered ? (
                          <Badge variant="secondary">
                            {i18n.t('Picking:detailDeliveredYes', {
                              date: item.delivered_at
                                ? formatDateTimeLocalized(
                                    new Date(item.delivered_at),
                                    i18n.language,
                                  )
                                : '',
                            })}
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            {item.delivered_amount > 0
                              ? `${item.delivered_amount} / ${item.amount}`
                              : i18n.t('Picking:detailDeliveredNo')}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        <OrderPagination
          currentPage={page}
          setCurrentPage={setPage}
          totalPages={totalPages}
        />
      </CardContent>
    </Card>
  );
}
